/**
 * Générateur — Déterminer un volume
 * Attendu ID : 649 | Niveau : 6ème | Domaine : Grandeurs et mesures
 *
 * Types générés : qcm_unique, texte, texte_trou_saisie, relier
 * Labels de référence :
 *   - Compter les cubes d'un assemblage y compris les cubes cachés (SVG)
 * Variantes estimées : 1000+
 */

import {
    randomChoice, randInt, shuffle,
    makeQcm, makeSaisie, makeRelier, makeTexteTrou,
} from '../../../utils';

// ─── Constantes ───
const ATTENDU_ID = 649;
const opts = (label) => ({ niveau: '6eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_VOL = 'Compter les cubes d\'un assemblage y compris les cubes cachés (SVG)';

// ─── Moteur SVG isométrique ───

const S = 28;
const SH = S * 0.6;

const isoPos = (c, r, l) => ({
    x: (c - r) * S,
    y: (c + r) * SH / 2 - l * SH,
});

const drawCube = (c, r, l) => {
    const { x, y } = isoPos(c, r, l);
    const top = `${x},${y - SH} ${x + S},${y - SH / 2} ${x},${y} ${x - S},${y - SH / 2}`;
    const left = `${x - S},${y - SH / 2} ${x},${y} ${x},${y + SH} ${x - S},${y + SH / 2}`;
    const right = `${x},${y} ${x + S},${y - SH / 2} ${x + S},${y + SH / 2} ${x},${y + SH}`;
    return `<polygon points="${top}" fill="#dbeafe" stroke="#1e293b" stroke-width="1"/>` +
           `<polygon points="${left}" fill="#93c5fd" stroke="#1e293b" stroke-width="1"/>` +
           `<polygon points="${right}" fill="#60a5fa" stroke="#1e293b" stroke-width="1"/>`;
};

const buildAssemblageSVG = (cubes, label) => {
    // Painter's algorithm : dessiner du plus lointain au plus proche.
    // Dans cette projection iso (+c = droite-avant, +r = gauche-avant, +l = haut),
    // la "profondeur" depuis le viewer est proportionnelle à (c+r) : plus elle est
    // élevée, plus le cube est proche (et doit être dessiné en dernier).
    const sorted = [...cubes].sort((a, b) => {
        if (a.l !== b.l) return a.l - b.l;
        if ((a.c + a.r) !== (b.c + b.r)) return (a.c + a.r) - (b.c + b.r);
        return a.r - b.r;
    });

    let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
    for (const { c, r, l } of cubes) {
        const { x, y } = isoPos(c, r, l);
        minX = Math.min(minX, x - S);
        maxX = Math.max(maxX, x + S);
        minY = Math.min(minY, y - SH);
        maxY = Math.max(maxY, y + SH);
    }

    const pad = 15;
    const labelH = label ? 22 : 0;
    const w = Math.ceil(maxX - minX + 2 * pad);
    const h = Math.ceil(maxY - minY + 2 * pad + labelH);
    const ox = -minX + pad;
    const oy = -minY + pad;

    // Signature compacte unique (pour l'audit : entre dans les 80 premiers chars du imageUrl)
    const sig = sorted.map(({ c, r, l }) => `${c}${r}${l}`).join('').slice(0, 30);

    let svg = `<svg id="a${sig}" viewBox="0 0 ${w} ${h}" xmlns="http://www.w3.org/2000/svg" style="max-width:${w}px;display:inline-block;vertical-align:top;margin:4px">`;
    svg += `<g transform="translate(${Math.round(ox)},${Math.round(oy)})">`;
    for (const { c, r, l } of sorted) svg += drawCube(c, r, l);
    svg += '</g>';
    if (label) svg += `<text x="${Math.round(w / 2)}" y="${h - 4}" text-anchor="middle" font-size="13" fill="currentColor">${label}</text>`;
    svg += '</svg>';
    return svg;
};

// ─── Pool d'assemblages avec info couches ───
// layers: tableau du nombre de cubes par couche (de bas en haut)

const ASSEMBLAGES = [
    // ─ Volume 4 ─
    { cubes: [{c:0,r:0,l:0},{c:1,r:0,l:0},{c:0,r:0,l:1},{c:1,r:0,l:1}], vol: 4, layers: [2, 2] },
    { cubes: [{c:0,r:0,l:0},{c:1,r:0,l:0},{c:2,r:0,l:0},{c:0,r:0,l:1}], vol: 4, layers: [3, 1] },
    { cubes: [{c:0,r:0,l:0},{c:1,r:0,l:0},{c:0,r:1,l:0},{c:1,r:1,l:0}], vol: 4, layers: [4] },
    // ─ Volume 5 ─
    { cubes: [{c:0,r:0,l:0},{c:1,r:0,l:0},{c:2,r:0,l:0},{c:0,r:0,l:1},{c:1,r:0,l:1}], vol: 5, layers: [3, 2] },
    { cubes: [{c:0,r:0,l:0},{c:1,r:0,l:0},{c:0,r:1,l:0},{c:1,r:1,l:0},{c:0,r:0,l:1}], vol: 5, layers: [4, 1] },
    // ─ Volume 6 ─
    { cubes: [{c:0,r:0,l:0},{c:1,r:0,l:0},{c:2,r:0,l:0},{c:0,r:0,l:1},{c:1,r:0,l:1},{c:0,r:0,l:2}], vol: 6, layers: [3, 2, 1] },
    { cubes: [{c:0,r:0,l:0},{c:1,r:0,l:0},{c:2,r:0,l:0},{c:0,r:1,l:0},{c:1,r:1,l:0},{c:2,r:1,l:0}], vol: 6, layers: [6] },
    { cubes: [{c:0,r:0,l:0},{c:1,r:0,l:0},{c:0,r:1,l:0},{c:1,r:1,l:0},{c:0,r:0,l:1},{c:1,r:0,l:1}], vol: 6, layers: [4, 2] },
    // ─ Volume 7 ─
    { cubes: [{c:0,r:0,l:0},{c:1,r:0,l:0},{c:2,r:0,l:0},{c:0,r:1,l:0},{c:0,r:0,l:1},{c:1,r:0,l:1},{c:2,r:0,l:1}], vol: 7, layers: [4, 3] },
    { cubes: [{c:0,r:0,l:0},{c:1,r:0,l:0},{c:2,r:0,l:0},{c:0,r:1,l:0},{c:1,r:1,l:0},{c:0,r:0,l:1},{c:0,r:1,l:1}], vol: 7, layers: [5, 2] },
    // ─ Volume 8 ─
    { cubes: [{c:0,r:0,l:0},{c:1,r:0,l:0},{c:0,r:1,l:0},{c:1,r:1,l:0},{c:0,r:0,l:1},{c:1,r:0,l:1},{c:0,r:1,l:1},{c:1,r:1,l:1}], vol: 8, layers: [4, 4] },
    { cubes: [{c:0,r:0,l:0},{c:1,r:0,l:0},{c:2,r:0,l:0},{c:3,r:0,l:0},{c:0,r:0,l:1},{c:1,r:0,l:1},{c:2,r:0,l:1},{c:3,r:0,l:1}], vol: 8, layers: [4, 4] },
    // ─ Volume 9 ─
    { cubes: [{c:0,r:0,l:0},{c:1,r:0,l:0},{c:2,r:0,l:0},{c:0,r:1,l:0},{c:1,r:1,l:0},{c:2,r:1,l:0},{c:0,r:0,l:1},{c:1,r:0,l:1},{c:2,r:0,l:1}], vol: 9, layers: [6, 3] },
    { cubes: [{c:0,r:0,l:0},{c:1,r:0,l:0},{c:2,r:0,l:0},{c:0,r:1,l:0},{c:1,r:1,l:0},{c:2,r:1,l:0},{c:0,r:2,l:0},{c:1,r:2,l:0},{c:2,r:2,l:0}], vol: 9, layers: [9] },
    // ─ Volume 10 ─
    { cubes: [{c:0,r:0,l:0},{c:1,r:0,l:0},{c:2,r:0,l:0},{c:0,r:1,l:0},{c:1,r:1,l:0},{c:2,r:1,l:0},{c:0,r:0,l:1},{c:1,r:0,l:1},{c:0,r:1,l:1},{c:1,r:1,l:1}], vol: 10, layers: [6, 4] },
    { cubes: [{c:0,r:0,l:0},{c:1,r:0,l:0},{c:2,r:0,l:0},{c:3,r:0,l:0},{c:4,r:0,l:0},{c:0,r:0,l:1},{c:1,r:0,l:1},{c:2,r:0,l:1},{c:3,r:0,l:1},{c:4,r:0,l:1}], vol: 10, layers: [5, 5] },
    // ─ Volume 12 ─
    { cubes: [{c:0,r:0,l:0},{c:1,r:0,l:0},{c:2,r:0,l:0},{c:0,r:1,l:0},{c:1,r:1,l:0},{c:2,r:1,l:0},{c:0,r:0,l:1},{c:1,r:0,l:1},{c:2,r:0,l:1},{c:0,r:1,l:1},{c:1,r:1,l:1},{c:2,r:1,l:1}], vol: 12, layers: [6, 6] },
    // ─ Volume 15 ─
    { cubes: [{c:0,r:0,l:0},{c:1,r:0,l:0},{c:2,r:0,l:0},{c:0,r:1,l:0},{c:1,r:1,l:0},{c:2,r:1,l:0},{c:0,r:2,l:0},{c:1,r:2,l:0},{c:2,r:2,l:0},{c:0,r:0,l:1},{c:1,r:0,l:1},{c:2,r:0,l:1},{c:0,r:1,l:1},{c:1,r:1,l:1},{c:2,r:1,l:1}], vol: 15, layers: [9, 6] },
];

/** Nombre de cubes visibles (approximation : cubes sans voisin au-dessus ni devant) */
const countVisible = (cubes) => {
    const key = (c, r, l) => `${c},${r},${l}`;
    const set = new Set(cubes.map(({ c, r, l }) => key(c, r, l)));
    let visible = 0;
    for (const { c, r, l } of cubes) {
        // Un cube est « caché » si un cube est au-dessus ET devant lui
        const hasAbove = set.has(key(c, r, l + 1));
        const hasFront = set.has(key(c, r - 1, l));
        const hasRight = set.has(key(c + 1, r, l));
        if (!(hasAbove && hasFront && hasRight)) visible++;
    }
    return visible;
};

// ─── Générateurs dynamiques d'assemblages ───

/** Parallélépipède rectangle (a × b × h). */
const genBoite = () => {
    let a, b, h;
    do {
        a = randInt(1, 4);
        b = randInt(1, 4);
        h = randInt(1, 4);
    } while (a * b * h < 2 || a * b * h > 20);
    const cubes = [];
    for (let l = 0; l < h; l++) {
        for (let r = 0; r < b; r++) {
            for (let c = 0; c < a; c++) {
                cubes.push({ c, r, l });
            }
        }
    }
    return { cubes, vol: a * b * h, layers: Array(h).fill(a * b) };
};

/** Base rectangulaire + colonne à un coin (forme en L / en T inversé). */
const genLShape = () => {
    const a = randInt(2, 4);
    const b = randInt(1, 3);
    const hColonne = randInt(1, 3);
    const vol = a * b + hColonne;
    if (vol > 20) return genLShape();
    const cubes = [];
    for (let r = 0; r < b; r++) {
        for (let c = 0; c < a; c++) {
            cubes.push({ c, r, l: 0 });
        }
    }
    for (let l = 1; l <= hColonne; l++) {
        cubes.push({ c: 0, r: 0, l });
    }
    const layers = [a * b, ...Array(hColonne).fill(1)];
    return { cubes, vol, layers };
};

/** Escalier : n marches de largeur w. */
const genEscalier = () => {
    const n = randInt(2, 4);
    const w = randInt(1, 2);
    const cubes = [];
    const layers = [];
    for (let l = 0; l < n; l++) {
        let countLayer = 0;
        for (let c = 0; c < n - l; c++) {
            for (let r = 0; r < w; r++) {
                cubes.push({ c, r, l });
                countLayer++;
            }
        }
        layers.push(countLayer);
    }
    const vol = cubes.length;
    if (vol > 20) return genEscalier();
    return { cubes, vol, layers };
};

/** Deux couches empilées de tailles différentes (pyramide tronquée). */
const genPyramide = () => {
    const a1 = randInt(2, 3), b1 = randInt(2, 3);  // base
    const a2 = randInt(1, a1), b2 = randInt(1, b1); // couche supérieure (réduite)
    if (a1 === a2 && b1 === b2) return genPyramide(); // évite boite pure
    const vol = a1 * b1 + a2 * b2;
    if (vol > 20) return genPyramide();
    const cubes = [];
    for (let r = 0; r < b1; r++) {
        for (let c = 0; c < a1; c++) {
            cubes.push({ c, r, l: 0 });
        }
    }
    for (let r = 0; r < b2; r++) {
        for (let c = 0; c < a2; c++) {
            cubes.push({ c, r, l: 1 });
        }
    }
    return { cubes, vol, layers: [a1 * b1, a2 * b2] };
};

/** Génère un assemblage aléatoire, pondéré par la richesse de chaque type.
 *  boite (~60 configs) 50%, L (27) 25%, pyramide (21) 15%, escalier (6) 10%. */
const pickOne = () => {
    const r = Math.random();
    if (r < 0.50) return genBoite();
    if (r < 0.75) return genLShape();
    if (r < 0.90) return genPyramide();
    return genEscalier();
};

/** Choisit N assemblages de volumes tous différents (utilise générateurs dynamiques + pool statique en backup). */
const pickNDistinct = (n) => {
    const result = [];
    const usedVols = new Set();
    // Essayer plusieurs fois avec le générateur dynamique
    for (let i = 0; i < 50 && result.length < n; i++) {
        const asm = pickOne();
        if (!usedVols.has(asm.vol)) {
            usedVols.add(asm.vol);
            result.push(asm);
        }
    }
    // Fallback sur le pool statique si besoin
    if (result.length < n) {
        const pool = shuffle([...ASSEMBLAGES]);
        for (const a of pool) {
            if (!usedVols.has(a.vol)) {
                usedVols.add(a.vol);
                result.push(a);
                if (result.length >= n) break;
            }
        }
    }
    return result;
};

// ═══════════════════════════════════════════
// EXERCICE — Déterminer le volume
// ═══════════════════════════════════════════

/** Composite : assemblages en grille (rangées de 2), UN seul SVG envoyé par le
 *  canal elements_additionnels (unification 648, revue 2026-07-31) — l'ancien
 *  <div flex> inline dans le texte échappait au moteur Print et au fingerprint
 *  d'unicité du harnais. */
const buildCompositeGrille = (assemblages, labels) => {
    const gap = 26;
    const pad = 14;
    const labelH = 22;
    const subs = assemblages.map((asm, i) => {
        let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
        for (const { c, r, l } of asm.cubes) {
            const { x, y } = isoPos(c, r, l);
            minX = Math.min(minX, x - S);
            maxX = Math.max(maxX, x + S);
            minY = Math.min(minY, y - SH);
            maxY = Math.max(maxY, y + SH);
        }
        const sorted = [...asm.cubes].sort((a, b) => {
            if (a.l !== b.l) return a.l - b.l;
            if ((a.c + a.r) !== (b.c + b.r)) return (a.c + a.r) - (b.c + b.r);
            return a.r - b.r;
        });
        return {
            w: Math.ceil(maxX - minX + 2 * pad),
            h: Math.ceil(maxY - minY + 2 * pad) + labelH,
            ox: -minX + pad,
            oy: -minY + pad,
            sorted,
            label: labels[i],
        };
    });

    // Grille : rangées de 2
    const rangées = [];
    for (let i = 0; i < subs.length; i += 2) rangées.push(subs.slice(i, i + 2));
    const wTotal = Math.max(...rangées.map(rg => rg.reduce((a, x) => a + x.w, 0) + gap * (rg.length - 1)));
    const hTotal = rangées.reduce((a, rg) => a + Math.max(...rg.map(x => x.h)), 0) + gap * (rangées.length - 1);

    const sig = assemblages.map(a => a.cubes.map(k => `${k.c}${k.r}${k.l}`).join('')).join('_').slice(0, 30);

    let svg = `<svg id="g${sig}" viewBox="0 0 ${wTotal} ${hTotal}" xmlns="http://www.w3.org/2000/svg" style="max-width:${wTotal}px;display:block;margin:4px auto">`;
    let yOff = 0;
    for (const rg of rangées) {
        const hRangée = Math.max(...rg.map(x => x.h));
        let xOff = 0;
        for (const sub of rg) {
            svg += `<g transform="translate(${Math.round(xOff + sub.ox)},${Math.round(yOff + sub.oy)})">`;
            for (const { c, r, l } of sub.sorted) svg += drawCube(c, r, l);
            svg += '</g>';
            svg += `<text x="${Math.round(xOff + sub.w / 2)}" y="${Math.round(yOff + sub.h - 6)}" text-anchor="middle" font-size="13" fill="currentColor">${sub.label}</text>`;
            xOff += sub.w + gap;
        }
        yOff += hRangée + gap;
    }
    svg += '</svg>';
    return svg;
};

/** Helper : convertit un assemblage SVG en élément image pour elements_additionnels.
 *  Important pour l'audit : le SVG inline est strippé du fingerprint (HTML tags removed),
 *  tandis qu'un imageUrl dans elements_additionnels contribue à l'unicité. */
const asmToElement = (cubes, label) => {
    const svg = buildAssemblageSVG(cubes, label);
    return { type: 'image', config: { imageUrl: `data:image/svg+xml,${encodeURIComponent(svg)}`, maxWidth: 'compact' } };
};

const genVolQcm = () => {
    const asm = pickOne();
    const texte = `<p>Quel est le volume de cet assemblage de cubes d'arête 1 cm ?</p>{{element:0}}`;
    const elAdd = { version: 1, elements: [asmToElement(asm.cubes, null)] };

    const vol = asm.vol;
    const visible = countVisible(asm.cubes);

    // Distracteurs : volume sans cachés (si différent), ±1, ±2, faces visibles approximation
    const dSet = new Set();
    if (visible !== vol) dSet.add(visible);
    dSet.add(vol + 1);
    dSet.add(vol - 1);
    dSet.add(vol + 2);
    dSet.add(vol * 2);
    dSet.delete(vol);
    dSet.delete(0);
    const dArr = shuffle([...dSet]).slice(0, 3);

    const choices = [vol, ...dArr].map(v => `${v} cm³`);
    const payload = makeQcm(choices, `${vol} cm³`, texte, { ...opts(L_VOL), elements_additionnels: elAdd });
    payload._meta.generatorKey = 'determiner_volume';
    return payload;
};

const genVolTexte = () => {
    const asm = pickOne();
    const texte = `<p>Détermine le volume de cet assemblage de cubes d'arête 1 cm, en cm³.</p>{{element:0}}`;
    const elAdd = { version: 1, elements: [asmToElement(asm.cubes, null)] };

    // strict RETIRÉ (revue 2026-07-31, doctrine 644/646) : la compétence est
    // le dénombrement, pas l'écriture du nombre.
    const payload = makeSaisie(String(asm.vol), texte, { ...opts(L_VOL), suffixe: 'cm³', elements_additionnels: elAdd });
    payload._meta.generatorKey = 'determiner_volume';
    return payload;
};

const genVolTrou = () => {
    // Choisir un assemblage avec au moins 2 couches pour le comptage couche par couche
    let asm;
    for (let i = 0; i < 20; i++) {
        asm = pickOne();
        if (asm.layers.length >= 2) break;
    }
    if (asm.layers.length < 2) {
        // Fallback : forcer la génération d'un assemblage multi-couches
        asm = genLShape();
    }
    const texte = `<p>Chaque cube a une arête de 1 cm. Détermine le volume de cet assemblage.</p>{{element:0}}`;
    const elAdd = { version: 1, elements: [asmToElement(asm.cubes, null)] };

    const layers = asm.layers;
    let phrase = '';
    if (layers.length === 2) {
        phrase = `Couche du bas : {{${layers[0]}}} cubes.\n`
            + `Couche du haut : {{${layers[1]}}} cubes.\n`
            + `Volume total : {{${asm.vol}}} cm³.`;
    } else if (layers.length === 3) {
        phrase = `Couche du bas : {{${layers[0]}}} cubes.\n`
            + `Couche du milieu : {{${layers[1]}}} cubes.\n`
            + `Couche du haut : {{${layers[2]}}} cube${layers[2] > 1 ? 's' : ''}.\n`
            + `Volume total : {{${asm.vol}}} cm³.`;
    } else {
        // Fallback pour plus de 3 couches (rare)
        const parts = layers.map((n, i) => `Couche ${i + 1} : {{${n}}} cube${n > 1 ? 's' : ''}`);
        phrase = parts.join('.\n') + `.\nVolume total : {{${asm.vol}}} cm³.`;
    }

    const payload = makeTexteTrou(phrase, texte, { ...opts(L_VOL), mode: 'saisie', strict: false, elements_additionnels: elAdd });
    payload._meta.generatorKey = 'determiner_volume';
    return payload;
};

const genVolRelier = () => {
    const items = pickNDistinct(4);
    const labels = ['A', 'B', 'C', 'D'];

    const pairs = items.map((item, i) => ({
        source: `Assemblage ${labels[i]}`,
        target: `${item.vol} cm³`,
    }));

    const composite = buildCompositeGrille(items, labels.map(l => `Assemblage ${l}`));
    const elAdd = {
        version: 1,
        elements: [{ type: 'image', config: { imageUrl: `data:image/svg+xml,${encodeURIComponent(composite)}`, maxWidth: 'large' } }],
    };

    const texte = `<p>Chaque cube a une arête de 1 cm. Relie chaque assemblage à son volume.</p>{{element:0}}`;
    const payload = makeRelier(pairs, texte, { ...opts(L_VOL), elements_additionnels: elAdd });
    payload._meta.generatorKey = 'determiner_volume';
    return payload;
};

// ─── Registre ───
const GENERATORS = {
    determiner_volume_qcm_unique:       genVolQcm,
    determiner_volume_texte:            genVolTexte,
    determiner_volume_texte_trou_saisie: genVolTrou,
    determiner_volume_relier:           genVolRelier,
};

// ─── Catégories ───
const FORMAT_LABELS = {
    qcm_unique: 'QCM', qcm_multiple: 'QCM Multi', texte: 'Saisie',
    texte_trou_drag: 'Drag', texte_trou_saisie: 'Saisie trous',
    relier: 'Relier', tableau_vf: 'Vrai/Faux', classer: 'Classer', ordonner: 'Ordonner',
};

const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter(k => k.startsWith(`${prefix}_`))
        .map(k => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'determiner_volume', label: L_VOL, formatsList: buildFormats('determiner_volume') },
];

// ─── Point d'entrée ───
export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) {
        key = typeExercice;
    } else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else {
        key = randomChoice(Object.keys(GENERATORS));
    }
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
